import React, { useState } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
    IconButton,
    Tooltip
} from '@mui/material';
import { Edit, Trash2 } from 'react-feather';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';

const ProblemTypeList = ({ problemTypes, onEdit, onDelete }) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [problemTypeToDelete, setProblemTypeToDelete] = useState(null);

    // Handle page change
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // Handle rows per page change
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Open delete confirmation dialog
    const openDeleteConfirm = (problemType) => {
        setProblemTypeToDelete(problemType);
        setDeleteConfirmOpen(true);
    };

    // Close delete confirmation dialog
    const closeDeleteConfirm = () => {
        setDeleteConfirmOpen(false);
        setProblemTypeToDelete(null);
    };

    // Confirm delete
    const confirmDelete = () => {
        if (problemTypeToDelete) {
            onDelete(problemTypeToDelete.id);
            closeDeleteConfirm();
        }
    };

    // Paginated problem types
    const paginatedProblemTypes = problemTypes.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <>
            <Paper
                elevation={0}
                sx={{
                    borderRadius: '10px',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    overflow: 'hidden',
                    mb: 3
                }}
            >
                <TableContainer>
                    <Table>
                        <TableHead sx={{ backgroundColor: '#F9FAFB' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Problem Type</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Date Created</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedProblemTypes.map((problemType) => (
                                <TableRow key={problemType.id} hover>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {problemType.problem_type}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {new Date(problemType.date_created).toLocaleString()}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <Tooltip title="Edit">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onEdit(problemType)}
                                                    sx={{ mr: 1 }}
                                                >
                                                    <Edit size={16} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => openDeleteConfirm(problemType)}
                                                    color="error"
                                                >
                                                    <Trash2 size={16} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {problemTypes.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                                        <Typography variant="body2" color="textSecondary">
                                            No problem types found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={problemTypes.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            {/* Delete Confirmation Dialog */}
            <ConfirmDeleteDialog
                open={deleteConfirmOpen}
                onClose={closeDeleteConfirm}
                onConfirm={confirmDelete}
                title="Delete Problem Type"
                message="Are you sure you want to delete this problem type?"
                warningMessage="This action cannot be undone. All categories associated with this problem type will also be deleted."
                itemContent={
                    problemTypeToDelete && (
                        <Typography variant="subtitle2">
                            {problemTypeToDelete.problem_type}
                        </Typography>
                    )
                }
            />
        </>
    );
};

export default ProblemTypeList;